import axios from "axios";
import createEnvironment from "../global"
import { refreshToken } from "./refreshToken";

let intervalId = null;
const SESSION_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hrs in ms

let current = SESSION_TIMEOUT;
const startSessionTimer = () => {
    if (intervalId) clearInterval(intervalId);
    current = SESSION_TIMEOUT;

    intervalId = setInterval(() => {
        current -= 1000;
        const mins = Math.floor(current / 1000 / 60);
        const secs = Math.floor((current / 1000) % 60);
        // console.log("Inactivity timer =", `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
        if (current <= 0) {
            clearInterval(intervalId);
            localStorage.clear();
            window.location.href = "/login";
        }
    }, 1000);
};

const ENV_VAR = createEnvironment();
const BASE_URL = ENV_VAR.BASE_URL;

const axiosInstance = axios.create({
    baseURL: BASE_URL,
    timeout: 120000,
});

let isRefreshing = false;
let failedQueue = []; // Will store failed requests in order

const processQueue = (error, token = null) => {
    failedQueue.forEach(promise => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });
    failedQueue = [];
};

// 🔹 Request Interceptor: attach token
axiosInstance.interceptors.request.use(
    async (config) => {
        startSessionTimer()
        const token = localStorage.getItem("access_token_web");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// 🔹 Response Interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        // In case backend embeds 401 in body with 200
        const originalRequest = response.config;
        if (
            (response?.data?.statusCode === 401 || response?.data?.status === 401) &&
            !originalRequest._retry &&
            !originalRequest.url?.includes("/auth/login/v2/refresh")
        ) {
            return handle401(originalRequest);
        }
        return response;
    },
    async (error) => {
        const originalRequest = error?.config || {};

        if (!error?.response) {
            return Promise.reject(error);
        }
        if (error.response.status !== 401) {
            return Promise.reject(error);
        }
        if (originalRequest._retry || originalRequest.url?.includes("/auth/login/v2/refresh")) {
            return Promise.reject(error);
        }

        return handle401(originalRequest);
    }
);

async function handle401(originalRequest) {
    if (isRefreshing) {
        // Put request in queue to retry later
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        }).then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            originalRequest._retry = true;
            return axiosInstance(originalRequest);
        });
    }
    // First request to trigger refresh
    originalRequest._retry = true;
    isRefreshing = true;
    try {
        const newToken = await refreshToken();
        // Store new token
        localStorage.setItem("access_token_web", newToken);
        // Resolve all queued requests
        processQueue(null, newToken);
        // Retry the original request now
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
    } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
    } finally {
        isRefreshing = false;
    }
}



export const PATHS = {
    GET_INSIGHTS: "/tdf/api/userBureauInsight/getInsight",
    GET_OTP: (number) => `/tdf/api/generate/otp`,
    GET_USER_TRACK: (number) => `/auth/userTrack/getUserTrack/${number}`,
    USER_GET_REPORT: "/tdf/api/user/getReport",
    POST_CONTACT_MSG: "/tdf/api/contactUsMsg",
    POST_REVOKECONSENT: "/tdf/api/user/revokeConsent",
    POST_AUTH_TOKEN: "/auth/login/web/token",
    POST_SAVE_USER_TRACK: "/auth/userTrack/saveUserTrack",    //need to test it for new user only
    POST_CALL_ME_BACK: "/tdf/api/user/callMeBack",
    POST_GETING_PAYMENT_LINK: "/tdf/api/user/getPaymentLink",
    POST_COUNTER_OFFER: "/tdf/api/counterOffer",
    POST_SETTLEMENT_LISTING: "/tdf/api/user/settlement/listing",
    GET_PROFILE_DETAIL: "/tdf/api/user/getProfileDetail",
    POST_ENHANCED_FETCH_REPORT: "/tdf/api/user/enhancedFetchReport",
    GET_GET_STATTE: "/tdf/api/getState",      //need to test
    POST_FETCH_REPORT: "/tdf/api/user/fetchReport",
    POST_USER_PROFILE: "/tdf/api/user/v2/submitProfile",
    POST_VALIDATE_CREDIT_OTP: "/tdf/api/user/validate/credit/otp",
    POST_GENERAL_INSIGHTS: (mob) => `/tdf/api/userBureauInsight/generalInsight`,
    POST_CALL_SETTLE_DETAILS: "/tdf/api/user/acDetail",
    POST_USER_SAVE_ACTION: "/tdf/api/userAction/saveAction",
    POST_ON_DEMAND_REFRESH_REPORT: "/tdf/api/user/onDemandRefresh",
    GET_TnC_CONSENT: "/auth/userTrack/getUserTnc",
    POST_USER_SUBMIT_AMOUNT: (moreBtn) => `/tdf/api/user/${moreBtn}`,
    DOWNLOAD_SETTLEMENT_LETTER: (id) => `/tdf/api/downloadSettlementLetter/${id}`,

    // 
    ENHANCED_FETCH_REPORT: '/tdf/api/user/enhancedMatch',
    NORMAL_FETCH_REPORT: '/tdf/api/user/singleAction',

};

// Authorization headers
const authorizeRequest = () => {
    const AUTH_RESPONSE = localStorage.getItem("TDFToken");
    return AUTH_RESPONSE
        ? { Authorization: `Bearer ${AUTH_RESPONSE}` }
        : {};
};

// error handler
const handleError = (err, cb) => {
    const errorMessage = err?.response?.data?.message || "An error occurred";
    const status = err?.response?.status || 500;
    console.error(`Error [${status}]:`, errorMessage);
    cb(null, { status, errorMessage });
};


// API methods
export const API = {
    GET: (url, body = {}, params = {}, cb) => {
        axiosInstance
            .get(url, {
                headers: authorizeRequest(),
                data: body,
                params,
            })
            .then((res) => cb(res, null))
            .catch((err) => handleError(err, cb));
    },

    POST: (url, body = {}, params = {}, cb) => {
        axiosInstance
            .post(url, body, {
                headers: authorizeRequest(),
                params,
            })
            .then((res) => cb(res, null))
            .catch((err) => handleError(err, cb));
    },

    PATCH: (url, body = {}, params = {}, cb) => {
        axiosInstance
            .patch(url, body, {
                headers: authorizeRequest(),
                params,
            })
            .then((res) => cb(res.data, null))
            .catch((err) => handleError(err, cb));
    },

    DELETE: (url, body = {}, cb) => {
        axiosInstance
            .delete(url, {
                headers: authorizeRequest(),
                data: body,
            })
            .then((res) => cb(res.data, null))
            .catch((err) => handleError(err, cb));
    },

    DOWNLOAD: (url, body = {}, params = {}, cb) => {
        axiosInstance
            .get(url, {
                responseType: "blob",
                headers: authorizeRequest(),
                data: body,
                params,
            })
            .then((res) => cb(res, null))
            .catch((err) => handleError(err, cb));
    },
    GENERAL: (url, method = "get", body = {}, params = {}, headers = {}, cb) => {
        axiosInstance({
            url: url,
            method: method,
            data: body,
            params: params,
            headers: headers
        }).then((res) => cb(res, null))
            .catch((err) => handleError(err, cb));
    },

    DOWNLOADWITHHEAD: (url, headers = {}, body = {}, cb) => {
        axios
            .post(url, body, {
                headers,
                responseType: "blob",
            })
            .then((res) => cb(res, null))
            .catch((err) => handleError(err, cb));
    },

    DOWNLOADPOST: (url, body = {}, cb) => {
        axiosInstance
            .post(url, body, {
                headers: authorizeRequest(),
                responseType: "blob",
            })
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "template.xlsx");
                document.body.appendChild(link);
                link.click();
                cb(res, null);
            })
            .catch((err) => handleError(err, cb));
    },
};
